import { createClient, SupabaseClient } from "@supabase/supabase-js";

// Helper to get env vars in both Vite and Node environments
const getEnv = (key: string) => {
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    return import.meta.env[key];
  }
  if (typeof process !== 'undefined' && process.env) {
    return process.env[key];
  }
  return undefined;
};

// Use environment variables for security
const supabaseUrl = getEnv('VITE_SUPABASE_URL') || "https://uyucjjbotjqotulknkfy.supabase.co";
const supabaseAnonKey = getEnv('VITE_SUPABASE_ANON_KEY') || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InV5dWNqamJvdGpxb3R1bGtua2Z5Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzI3ODU5MTIsImV4cCI6MjA0ODM2MTkxMn0.SdmaKF8mWiM22JNh1s7LnV8lJskPKoJA6swqecjgrZ4";
const storageKey = `sb-${supabaseUrl.split('//')[1].split('.')[0]}-auth-token`;

/**
 * Supabase client instance configured with authentication settings
 *
 * Note: User ID and tenant ID are automatically extracted from the JWT token
 * on the server side using PostgreSQL functions (auth.uid() and auth.jwt() -> tenant_id)
 */
export const supabase: SupabaseClient = createClient(
    supabaseUrl,
    supabaseAnonKey,
    {
        auth: {
            storageKey: storageKey,
            persistSession: true,
            autoRefreshToken: true,
            detectSessionInUrl: true,
            flowType: 'pkce',
            storage: typeof window !== 'undefined' ? window.localStorage : undefined
        },
        global: {
            headers: {
                // Add proper headers to avoid 406 Not Acceptable errors
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                // No need to manually set tenant_id or user_id in headers
                // These are extracted from JWT on the server side
            },
        },
    }
);

/**
 * Handle authentication state changes
 * Logs user sign-in and sign-out events and ensures token validity
 */
supabase.auth.onAuthStateChange((event, session) => {
    if (process.env.NODE_ENV === 'development') {
        console.log('Auth state changed:', event);
        if (event === 'SIGNED_IN') {
            console.log('User signed in:', session?.user?.email);
        } else if (event === 'SIGNED_OUT') {
            console.log('User signed out');
        } else if (event === 'TOKEN_REFRESHED') {
            console.log('Token refreshed');
        }
    }

    // Ensure we have a valid session
    if (session) {
        // Check if token is about to expire (within 5 minutes)
        const expiresAt = session.expires_at;
        const now = Math.floor(Date.now() / 1000);
        const fiveMinutesInSeconds = 5 * 60;

        if (expiresAt && (expiresAt - now < fiveMinutesInSeconds)) {
            // Token is about to expire, refresh it
            supabase.auth.refreshSession();
        }
    }
});

import { invalidateUserQueries } from '@/services/query/user-queries';
import { QueryClient } from '@tanstack/react-query';

// Create a query client instance for use in non-component contexts
const queryClient = new QueryClient();

/**
 * Clears the user session and all authentication data
 * Invalidates all user-related queries
 * Redirects to the auth page after sign out
 */
export const clearSession = async (): Promise<void> => {
    try {
        // Invalidate all user-related queries in the cache
        invalidateUserQueries(queryClient);

        // Sign out from Supabase
        await supabase.auth.signOut();

        // Clear local storage auth data
        localStorage.removeItem(storageKey);

        // Clear any other auth-related items
        const authKeys = Object.keys(localStorage).filter(key =>
            key.includes('supabase') ||
            key.includes('sb-') ||
            key.includes('auth')
        );

        authKeys.forEach(key => localStorage.removeItem(key));

        // Redirect to auth page
        window.location.href = '/auth';
    } catch (error) {
        console.error('Error clearing session:', error);
        // Despite error, still try to redirect
        window.location.href = '/auth';
    }
};

